'use client';

import { createBrowserClient } from '@supabase/ssr';

// Session sharing across unitedfxa.com, client.unitedfxa.com and
// trade.unitedfxa.com depends on all three apps writing the auth cookie the same
// way. This file must stay identical in all three repos.

const STORAGE_PREFIX = 'sb_';

/**
 * The cookie Domain to use, or undefined for a host-only cookie.
 *
 * Sending `Domain=.unitedfxa.com` from a host that is not under that domain
 * makes the browser reject the cookie outright, which signs everyone out. So a
 * mismatch degrades to a host-only cookie rather than breaking auth — that is
 * what makes NEXT_PUBLIC_COOKIE_DOMAIN safe to set before DNS is cut over.
 */
function sharedCookieDomain(): string | undefined {
  const configured = process.env.NEXT_PUBLIC_COOKIE_DOMAIN?.trim();
  if (!configured || typeof window === 'undefined') return undefined;

  const bare = configured.replace(/^\./, '');
  if (!bare) return undefined;

  const host = window.location.hostname;
  if (host === bare || host.endsWith(`.${bare}`)) return `.${bare}`;
  return undefined;
}

const isSecure = () => typeof window !== 'undefined' && window.location.protocol === 'https:';

/** Plain http on localhost cannot set Secure cookies, so fall back to storage. */
const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;

    const probe = '__sb_probe__';
    const attrs = `Path=/; SameSite=Lax${isSecure() ? '; Secure' : ''}`;
    document.cookie = `${probe}=1; ${attrs}`;
    cache = document.cookie.includes(probe);
    document.cookie = `${probe}=; Max-Age=0; ${attrs}`;
    return cache;
  };
})();

const readCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((raw) => {
          const c = raw.trim();
          const idx = c.indexOf('=');
          return idx === -1
            ? { name: c, value: '' }
            : { name: c.slice(0, idx).trim(), value: decodeURIComponent(c.slice(idx + 1)) };
        })
        .filter((c) => c.name);

const readStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(STORAGE_PREFIX))
      .map((k) => ({ name: k.slice(STORAGE_PREFIX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

function writeCookie(name: string, value: string, options?: any) {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Path=${options?.path || '/'}`,
    'SameSite=Lax',
  ];

  if (isSecure()) parts.push('Secure');

  const domain = sharedCookieDomain();
  if (domain) parts.push(`Domain=${domain}`);

  if (options?.maxAge) parts.push(`Max-Age=${options.maxAge}`);
  if (options?.expires) parts.push(`Expires=${new Date(options.expires).toUTCString()}`);

  document.cookie = parts.join('; ');
}

function deleteCookie(name: string) {
  if (typeof document === 'undefined') return;

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const configured = process.env.NEXT_PUBLIC_COOKIE_DOMAIN?.trim().replace(/^\./, '');

  // Cookies set under any of these Domain values must all be cleared, otherwise
  // a stale one shadows the deletion and the user appears logged in.
  const domains = ['', host, host ? `.${host}` : '', configured ? `.${configured}` : ''].filter(
    (d, i, all) => all.indexOf(d) === i
  );

  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=Lax; Secure',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];

  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;
    domains.filter(Boolean).forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
}

/** Clear every Supabase auth cookie and storage key, including chunked tokens. */
export function clearAuthStorage() {
  if (typeof document === 'undefined') return;

  readCookies()
    .map((c) => c.name)
    .filter((n) => n.startsWith('sb-') || n.startsWith(STORAGE_PREFIX) || n.includes('auth-token'))
    .forEach(deleteCookie);

  [localStorage, sessionStorage].forEach((store) => {
    try {
      Object.keys(store)
        .filter(
          (k) =>
            k.startsWith(STORAGE_PREFIX) ||
            k.startsWith('sb-') ||
            k.includes('supabase') ||
            k.includes('auth-token')
        )
        .forEach((k) => store.removeItem(k));
    } catch {
      // private mode / quota — cookies above are the important part
    }
  });
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? readCookies() : readStorage()),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;

          cookiesToSet.forEach(({ name, value, options }) => {
            if (canUseCookies()) {
              if (value) {
                writeCookie(name, value, options);
              } else {
                deleteCookie(name);
              }
              return;
            }
            try {
              if (value) {
                localStorage.setItem(`${STORAGE_PREFIX}${name}`, value);
              } else {
                localStorage.removeItem(`${STORAGE_PREFIX}${name}`);
              }
            } catch {
              // private mode / quota
            }
          });
        },
      },
    }
  );
}
